import { useEffect, useRef, useState } from 'react';
import cx from 'clsx';
import { useOutsideClick } from '@kcd/design-system';
import { trackEvent } from '@kcd/event-tracker';

import { useRouterQueryByKey, useUpdateRouterQuery } from '~/hooks/url';

import { ChevronDownOutlineIcon24x24 } from '~/assets/icons';

type SORT_TYPE_TITLE = '인기순' | '추천순' | '낮은가격순' | '최신순';
type SORT_TYPE_VALUE =
  | 'SCORE'
  | 'DISPLAY_RANK'
  | 'LOW_PRICE'
  | 'LATEST_PRODUCTS';

interface SORT_LIST_TYPE {
  title: SORT_TYPE_TITLE;
  value: SORT_TYPE_VALUE;
}

const SORT_LIST: SORT_LIST_TYPE[] = [
  {
    title: '인기순',
    value: 'SCORE',
  },
  {
    title: '추천순',
    value: 'DISPLAY_RANK',
  },
  {
    title: '낮은가격순',
    value: 'LOW_PRICE',
  },
  {
    title: '최신순',
    value: 'LATEST_PRODUCTS',
  },
];

export const SORT_LIST_OBJECT: { [key: string]: SORT_TYPE_TITLE } = {
  SCORE: '인기순',
  DISPLAY_RANK: '추천순',
  LOW_PRICE: '낮은가격순',
  LATEST_PRODUCTS: '최신순',
};

const ProductDisplaySortType = ({
  sortType,
}: {
  sortType: SORT_TYPE_VALUE;
}) => {
  const sortTypeFromQuery = useRouterQueryByKey('sortType', 'string');
  const updateRouterQuery = useUpdateRouterQuery();
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [opened, setOpened] = useState<boolean>(false);
  const [localSortType, setLocalSortType] = useState<SORT_TYPE_VALUE>(sortType);

  useOutsideClick({
    ref: wrapperRef,
    onClick: () => {
      if (opened) {
        setOpened(false);
      }
    },
  });

  const onClickSortType = (value: SORT_TYPE_VALUE) => {
    trackEvent('clicked dropdown in 마켓', {
      placementName: '상품필터링',
      path: window.location.pathname,
      clickedType: SORT_LIST_OBJECT[value],
    });
    setLocalSortType(value);
    void updateRouterQuery({ sortType: value });
    setOpened(false);
  };

  useEffect(() => {
    if (sortTypeFromQuery) {
      setLocalSortType(sortTypeFromQuery as SORT_TYPE_VALUE);
    }
  }, [sortTypeFromQuery]);

  const onClickFilterButton = () => {
    setOpened((prev) => {
      if (!prev) {
        trackEvent('viewed dropdown in 마켓', {
          placementName: '상품필터링',
          path: window.location.pathname,
        });
      }
      return !prev;
    });
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        type="button"
        className="flex h-[22px] items-center gap-4"
        onClick={onClickFilterButton}
      >
        <span className="text-gray-1000 typo-paragraph100-semibold">
          {SORT_LIST_OBJECT[localSortType]}
        </span>
        <ChevronDownOutlineIcon24x24
          className={cx('size-20 rotate-0', opened && '!rotate-180')}
        />
      </button>
      {opened && (
        <ul className="absolute left-[-10px] top-0 z-20 mt-[34px] whitespace-nowrap rounded-lg bg-gray-000 px-12 py-[7px] shadow-[2px_8px_20px_0px_#0000001A]">
          {SORT_LIST.map(({ title, value }) => {
            const isSelected = localSortType === value;

            return (
              <li
                role="presentation"
                key={value}
                className={cx(
                  'py-8 typo-paragraph100-regular text-gray-1000',
                  isSelected && '!text-blue-600 !typo-paragraph100-semibold',
                )}
                onClick={() => onClickSortType(value)}
              >
                {title}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};
export default ProductDisplaySortType;
